import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Bell, KeyRound, LogOut, Menu, X, Coffee, Fingerprint } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { InstallPwaButton } from "@/components/InstallPwaButton";
import { EnableNotificationsButton } from "@/components/EnableNotificationsButton";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { ManagePasskeysDialog } from "@/components/ManagePasskeysDialog";
import { SavePasskeyPrompt } from "@/components/SavePasskeyPrompt";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";

const employeeLinks = [
  { to: "/clock", label: "Dashboard", end: true },
  { to: "/timesheet", label: "Timesheet" },
  { to: "/leave", label: "Leave" },
  { to: "/payslips", label: "Payslips" },
  { to: "/incidents", label: "Incident Report" },
  { to: "/wastage", label: "Wastage", requiresWastage: true },
  { to: "/ordering", label: "Ordering", requiresOrdering: true },
  { to: "/expenses", label: "Expenses", requiresExpenses: true },
  { to: "/recipes", label: "Recipes" },
  { to: "/sales", label: "Sales", requiresSales: true },
];

// Primary admin tabs — always visible. Secondary tabs live under "More" to
// stop the nav overflowing on 13"–14" laptops.
const adminLinks = [
  { to: "/admin", label: "Admin Home", end: true },
  { to: "/admin/manager/dashboard", label: "Manager" },
  { to: "/admin/employees", label: "Employees" },
  { to: "/admin/timesheets", label: "Timesheets" },
  { to: "/admin/sales", label: "Sales" },
  { to: "/admin/leave", label: "Leave" },
  { to: "/admin/variance", label: "Variance" },
  { to: "/admin/settings", label: "Settings" },
];

// Secondary admin tabs — collapsed under the "More" dropdown so the nav
// stays compact below ~1500 px. Audit Log and Xero Sync are nested inside
// Settings.
const adminLinksMore = [
  { to: "/admin/documents", label: "Documents" },
  { to: "/admin/wastage", label: "Food Wastage" },
  { to: "/admin/ordering", label: "Ordering Sheet" },
  { to: "/admin/site-incidents", label: "Site Incidents" },
  { to: "/admin/expenses", label: "Expenses" },
  { to: "/recipes", label: "Recipes" },
  { to: "/admin/visas", label: "Visas" },
  { to: "/admin/trials", label: "Trials" },
];

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = user && (user.role === "ADMIN" || user.role === "SUPERADMIN");
  const isAdminArea = location.pathname.startsWith("/admin");
  const brandHref = isAdmin ? "/admin" : "/clock";
  // Voluntary password change is available for admin/manager accounts via
  // the user dropdown. Employees authenticate by PIN so they don't see it.
  const isStaffPortalUser = user && user.role !== "EMPLOYEE";
  const [changePwOpen, setChangePwOpen] = useState(false);

  const loadNotifs = async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotifs(data.notifications || []);
      setUnread(data.unread || 0);
    } catch (err) {
      // 401 = unauthenticated polling tick fired right after logout.
      // Other errors are transient (server restart, network blip) — the
      // 30s interval will retry. Don't toast either case so the user
      // isn't bothered while the polling self-heals.
      if (err?.response?.status !== 401 && typeof console !== "undefined") {
        // eslint-disable-next-line no-console -- intentional dev/operator diagnostic
        console.warn("Notifications poll failed:", err?.message || err);
      }
    }
  };

  useEffect(() => {
    if (user) loadNotifs();
    const i = setInterval(() => { if (user) loadNotifs(); }, 30000);
    return () => clearInterval(i);
  }, [user]);

  const markAllRead = async () => {
    await api.post("/notifications/mark-read", {});
    loadNotifs();
  };

  const canViewSales = user?.can_view_sales || ["ADMIN", "SUPERADMIN"].includes(user?.role);
  const filterSales = (arr) => arr.filter((l) => {
    if (l.to === "/admin/sales" || l.requiresSales) return canViewSales;
    if (l.requiresWastage) return !!user?.can_food_wastage;
    if (l.requiresOrdering) return !!user?.can_ordering;
    if (l.requiresExpenses) return !!user?.can_expenses;
    return true;
  });
  const links = filterSales(isAdminArea && isAdmin ? adminLinks : employeeLinks);
  const moreLinks = isAdminArea && isAdmin ? adminLinksMore : [];
  const isMoreActive = moreLinks.some((l) => location.pathname.startsWith(l.to));

  return (
    <div className="min-h-screen bg-[#F9FAFB] text-[#111827] font-body">
      <nav className="bg-white/90 backdrop-blur-md border-b border-gray-200 sticky top-0 z-40" data-testid="main-nav">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 gap-4">
            <div className="flex items-center gap-5 min-w-0">
              <Link to={brandHref} className="flex items-center gap-3 shrink-0" data-testid="brand-link">
                <img
                  src="/logo.png"
                  alt="Coffee Lovers Café"
                  className="h-11 w-11 rounded-lg object-cover shadow-sm"
                />
                <div className="leading-tight hidden sm:block">
                  <div className="font-logo text-lg font-semibold text-[#1A2744] whitespace-nowrap">Coffee Lovers Café</div>
                  <div className="text-[10px] uppercase tracking-widest text-[#C9A84C] -mt-0.5 whitespace-nowrap">Staff Portal</div>
                </div>
              </Link>
              <div className="hidden lg:flex items-center gap-0.5 min-w-0">
                {links.map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    end={l.end}
                    data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={({ isActive }) =>
                      `px-2.5 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                        isActive
                          ? "bg-[#1A2744] text-white"
                          : "text-[#1A2744]/80 hover:bg-[#1A2744]/5"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                ))}
                {moreLinks.length > 0 && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        data-testid="nav-more"
                        className={`px-2.5 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                          isMoreActive
                            ? "bg-[#1A2744] text-white"
                            : "text-[#1A2744]/80 hover:bg-[#1A2744]/5"
                        }`}
                      >
                        More ▾
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-56">
                      {moreLinks.map((l) => (
                        <DropdownMenuItem
                          key={l.to}
                          onSelect={() => navigate(l.to)}
                          data-testid={`nav-more-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                          {l.label}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                {isAdmin && !isAdminArea && (
                  <NavLink
                    to="/admin"
                    data-testid="nav-switch-admin"
                    className="px-2.5 py-2 rounded-lg text-sm font-medium text-[#C9A84C] hover:bg-[#C9A84C]/10 whitespace-nowrap"
                  >
                    Admin →
                  </NavLink>
                )}
                {isAdmin && isAdminArea && (
                  <NavLink
                    to="/clock"
                    data-testid="nav-switch-employee"
                    className="px-2.5 py-2 rounded-lg text-sm font-medium text-[#C9A84C] hover:bg-[#C9A84C]/10 whitespace-nowrap"
                  >
                    ← Employee
                  </NavLink>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="relative"
                    data-testid="notifications-bell"
                  >
                    <Bell className="h-5 w-5 text-[#1A2744]" strokeWidth={1.5} />
                    {unread > 0 && (
                      <span
                        className="absolute top-1 right-1 h-4 min-w-[16px] px-1 rounded-full bg-[#C9A84C] text-[10px] font-bold text-white flex items-center justify-center"
                        data-testid="notifications-badge"
                      >
                        {unread}
                      </span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-auto">
                  <DropdownMenuLabel className="flex items-center justify-between">
                    <span>Notifications</span>
                    {unread > 0 && (
                      <button
                        onClick={markAllRead}
                        className="text-xs text-[#C9A84C] hover:underline"
                        data-testid="notifications-mark-all"
                      >
                        Mark all read
                      </button>
                    )}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {notifs.length === 0 && (
                    <div className="px-3 py-6 text-center text-sm text-gray-500">
                      No notifications
                    </div>
                  )}
                  {notifs.slice(0, 12).map((n) => (
                    <DropdownMenuItem
                      key={n.id}
                      className={`flex flex-col items-start gap-1 py-2 ${
                        n.is_read ? "opacity-60" : ""
                      }`}
                      onClick={() => n.link && navigate(n.link)}
                    >
                      <span className="text-sm">{n.message}</span>
                      <span className="text-[11px] text-gray-400">
                        {new Date(n.created_at).toLocaleString()}
                      </span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="gap-2 hidden sm:flex"
                    data-testid="user-menu-trigger"
                  >
                    <div className="h-7 w-7 rounded-full bg-[#1A2744] text-[#C9A84C] flex items-center justify-center text-xs font-semibold">
                      {user?.name?.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                    </div>
                    <div className="text-left leading-tight hidden md:block">
                      <div className="text-sm font-medium text-[#1A2744]">
                        {user?.name}
                      </div>
                      <div className="text-[10px] text-gray-500 uppercase tracking-wider">
                        {user?.role}
                      </div>
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>
                    <div className="font-medium">{user?.name}</div>
                    <div className="text-xs text-gray-500">{user?.email}</div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={(e) => e.preventDefault()}
                    className="p-0"
                  >
                    <InstallPwaButton variant="ghost" className="w-full justify-start px-2 py-1.5" />
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={(e) => e.preventDefault()}
                    className="p-0 flex-col items-stretch"
                  >
                    <EnableNotificationsButton variant="ghost" />
                  </DropdownMenuItem>
                  {isStaffPortalUser && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => setChangePwOpen(true)}
                        data-testid="change-password-menu"
                      >
                        <KeyRound className="h-4 w-4 mr-2" /> Change password
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <ManagePasskeysDialog
                    trigger={
                      <DropdownMenuItem
                        onSelect={(e) => e.preventDefault()}
                        data-testid="manage-passkeys-menu"
                      >
                        <Fingerprint className="h-4 w-4 mr-2" /> Passkeys
                      </DropdownMenuItem>
                    }
                  />
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={async () => {
                      await logout();
                      navigate("/login");
                    }}
                    data-testid="logout-button"
                  >
                    <LogOut className="h-4 w-4 mr-2" /> Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={() => setMobileOpen((v) => !v)}
                data-testid="mobile-menu-toggle"
              >
                {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
            </div>
          </div>

          {mobileOpen && (
            <div className="lg:hidden pb-3 flex flex-col gap-1">
              {[...links, ...moreLinks].map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.end}
                  onClick={() => setMobileOpen(false)}
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `px-3 py-2 rounded-lg text-sm font-medium ${
                      isActive
                        ? "bg-[#1A2744] text-white"
                        : "text-[#1A2744]/80 hover:bg-[#1A2744]/5"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              {isAdmin && (
                <NavLink
                  to={isAdminArea ? "/clock" : "/admin"}
                  onClick={() => setMobileOpen(false)}
                  className="px-3 py-2 rounded-lg text-sm font-medium text-[#C9A84C]"
                >
                  {isAdminArea ? "← Employee view" : "Admin panel →"}
                </NavLink>
              )}
              <button
                onClick={async () => {
                  await logout();
                  navigate("/login");
                }}
                className="text-left px-3 py-2 rounded-lg text-sm text-red-600 hover:bg-red-50"
                data-testid="mobile-logout"
              >
                Log out
              </button>
            </div>
          )}
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-32 md:pb-8">
        {children}
      </main>
      <AppFooter />

      {/* One-time "save passkey" prompt — auto-dismisses after first decision
          and remembers cooldown across sessions. */}
      <SavePasskeyPrompt />

      {/* Voluntary "Change password" entry — opened from the user dropdown
          (admin/manager only; employees authenticate via PIN). */}
      {isStaffPortalUser && (
        <ChangePasswordDialog
          open={changePwOpen}
          onClose={() => setChangePwOpen(false)}
          forced={false}
        />
      )}
    </div>
  );
}

export function AppFooter() {
  return (
    <footer className="border-t border-gray-200 bg-white/60" data-testid="app-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 text-center text-[11px] text-gray-500">
        Developed and designed by{" "}
        <span className="font-semibold text-[#1A2744]">Carattere Technologies Limited</span>
      </div>
    </footer>
  );
}

export function StatusPill({ status }) {
  const map = {
    ACTIVE: "bg-emerald-100 text-emerald-800 border-emerald-200",
    COMPLETE: "bg-gray-100 text-gray-700 border-gray-200",
    APPROVED: "bg-[#1A2744]/10 text-[#1A2744] border-[#1A2744]/20",
    INCOMPLETE: "bg-amber-100 text-amber-800 border-amber-200",
    FLAGGED: "bg-red-100 text-red-800 border-red-200",
    DISPUTED: "bg-red-100 text-red-800 border-red-200",
    PENDING: "bg-amber-100 text-amber-800 border-amber-200",
    DECLINED: "bg-red-100 text-red-800 border-red-200",
    SYNCED: "bg-emerald-100 text-emerald-800 border-emerald-200",
    FAILED: "bg-red-100 text-red-800 border-red-200",
    NONE: "bg-gray-100 text-gray-600 border-gray-200",
  };
  return (
    <Badge
      variant="outline"
      className={`text-[10px] uppercase tracking-wider ${map[status] || "bg-gray-100"}`}
      data-testid={`status-pill-${status?.toLowerCase()}`}
    >
      {status}
    </Badge>
  );
}

export const SITE_LABEL = { SITE_1: "Christchurch", SITE_2: "Wellington" };
